import { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { toast } from "sonner";
import { CheckCircle2, LogOut, Search, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api, errMsg, fmtDate, fmtDateOnly } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Field, TextInput, TextArea, SelectInput, ActionButton, FormDialog } from "../../components/common";
import MiniMap from "../../components/MiniMap";
import { RequesterPicker } from "../../components/RequesterPicker";
import { LocationPicker } from "../../components/LocationPicker";
import { WellBillingEditor } from "../../components/WellBillingEditor";
import { RentalEditForm } from "../../components/fractank/RentalEditForm";
import { useMapsLink } from "../../hooks/useMapsLink";
import { Input } from "../../components/ui/input";

import { EMPTY_RENTAL as EMPTY, JOB_TYPE_OPTIONS } from "../../lib/rentalDispatch";
import { RentalTypeBadge } from "../../components/fractank/RentalTypeBadge";
import { RentalPhotos } from "../../components/fractank/RentalPhotos";

const OnRentCard = ({ dispatch: d, onSchedulePickup, onEdit, pickupRequest }) => (
  <article data-testid={`mobile-rental-on-rent-${d.id}`} className={`border rounded-lg p-3 space-y-2 min-w-0 [overflow-wrap:anywhere] ${d.rental_type === "mixing_plant" ? "bg-pink-50 border-pink-300" : "bg-card border-slate-700"}`}>
    <div className="flex items-center justify-between gap-2">
      <span data-testid={`mobile-rental-on-rent-number-${d.id}`} className="font-mono text-sm text-amber-500">{d.dispatch_number}</span>
      <RentalTypeBadge job={d} prefix="sales-onrent" />
      <span data-testid={`mobile-rental-on-rent-status-${d.id}`} className="shrink-0 text-[10px] font-mono uppercase tracking-wider text-emerald-600 bg-emerald-500/10 border border-emerald-500/30 rounded px-1.5 py-0.5">On Rent</span>
    </div>
    <p data-testid={`mobile-rental-on-rent-customer-${d.id}`} className="text-sm font-medium text-slate-100">{d.customer_name}</p>
    <p data-testid={`mobile-rental-on-rent-location-${d.id}`} className="text-xs text-slate-400">{d.delivery_location || d.geo_address || "No location"}</p>
    {d.billing_start_date && <p data-testid={`mobile-rental-on-rent-billing-${d.id}`} className="text-xs font-mono text-slate-500">Billing starts {fmtDate(d.billing_start_date)}</p>}
    {(d.asset_tags || []).length > 0 && (
      <div data-testid={`mobile-rental-on-rent-equipment-${d.id}`} className="flex flex-wrap gap-1.5">
        {d.asset_tags.map((tag, i) => <span key={`${d.asset_ids?.[i] || tag}-${i}`} data-testid={`mobile-rental-on-rent-equipment-${d.id}-${i}`} className="max-w-full rounded border border-slate-700 bg-slate-900 px-2 py-0.5 text-xs font-mono text-slate-300">{tag}</span>)}
      </div>
    )}
    {(d.drivers?.length > 0 || d.driver_name) && <p data-testid={`mobile-rental-on-rent-drivers-${d.id}`} className="text-xs text-slate-500">Driver: {(d.drivers?.length ? d.drivers : [d.driver_name]).join(", ")}</p>}
    {d.company_man && <p data-testid={`mobile-rental-on-rent-contact-${d.id}`} className="text-xs text-slate-500">Requester: {d.company_man}</p>}
    {(d.sales_rep || d.created_by_name) && <p data-testid={`mobile-rental-on-rent-rep-${d.id}`} className="text-xs text-slate-500">Rep: {d.sales_rep || d.created_by_name}</p>}
    {d.notes && <p data-testid={`mobile-rental-on-rent-notes-${d.id}`} className="text-xs text-slate-500">{d.notes}</p>}
    {d.lat != null && d.lng != null && <MiniMap testId={`mobile-rental-on-rent-map-${d.id}`} lat={d.lat} lng={d.lng} />}
    {pickupRequest && <p data-testid={`mobile-rental-pickup-requested-${d.id}`} className="text-xs text-emerald-600">Pickup requested{pickupRequest.pickup_date ? ` for ${fmtDateOnly(pickupRequest.pickup_date)}` : ""} · {pickupRequest.dispatch_number}</p>}
    <RentalPhotos job={d} canUpload prefix="sales" />
    <ActionButton type="button" variant="secondary" testId={`mobile-rental-edit-onrent-${d.id}`} onClick={() => onEdit(d)} className="w-full justify-center !py-3">
      Edit Tank Days / Wells
    </ActionButton>
    <ActionButton type="button" testId={`mobile-rental-schedule-pickup-${d.id}`} onClick={() => onSchedulePickup(d)} disabled={!!pickupRequest} className="w-full justify-center !py-3">
      Schedule Pickup
    </ActionButton>
  </article>
);

export default function MobileRental() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [dispatches, setDispatches] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [mapsLink, setMapsLink] = useState("");
  const resolving = useMapsLink(mapsLink, setForm);
  const [done, setDone] = useState(null);
  const [showMyRentals, setShowMyRentals] = useState(false);
  const [showOnRent, setShowOnRent] = useState(false);
  const [rentalsLoaded, setRentalsLoaded] = useState(false);
  const [rentalsError, setRentalsError] = useState("");
  const [pickupFor, setPickupFor] = useState(null);
  const [editOnRent, setEditOnRent] = useState(null);
  const [editNew, setEditNew] = useState(null);
  const [deleteNew, setDeleteNew] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [pickupDate, setPickupDate] = useState("");
  const [pickupNotes, setPickupNotes] = useState("");
  const [pickupBillingEnd, setPickupBillingEnd] = useState("");
  const [transferMode, setTransferMode] = useState(false);
  const [transferSourceId, setTransferSourceId] = useState("");
  const [transferBillingEnd, setTransferBillingEnd] = useState("");
  const [transferConfirmation, setTransferConfirmation] = useState(null);
  const transferRequest = useRef(null);
  const [pickupBusy, setPickupBusy] = useState(false);
  const [pickupError, setPickupError] = useState("");
  const [rentalSearch, setRentalSearch] = useState("");

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const loadDispatches = useCallback(() => api.get("/fractank/dispatches")
    .then((r) => { setDispatches(r.data); setRentalsError(""); })
    .catch((e) => setRentalsError(errMsg(e)))
    .finally(() => setRentalsLoaded(true)), []);

  const loadData = useCallback(() => {
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    loadDispatches();
  }, [loadDispatches]);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadDispatches, 10000);
    return () => clearInterval(interval);
  }, [loadData, loadDispatches]);

  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    if (link) link.setAttribute("href", "/manifest-rental.json");
    return () => {
      if (link) link.setAttribute("href", "/manifest.json");
    };
  }, []);

  useEffect(() => {
    const shared = new URLSearchParams(window.location.search).get("shared");
    if (shared) setMapsLink(shared);
  }, []);


  const onRentJobs = useMemo(() => dispatches
    .filter((d) => d.status === "on_rent")
    .sort((a, b) => (a.customer_name || "").localeCompare(b.customer_name || "")), [dispatches]);
  const myNewRentals = useMemo(() => dispatches
    .filter((d) => d.created_by === user?.id && (d.job_type || "new_rental") === "new_rental" && d.status === "scheduled")
    .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")), [dispatches, user?.id]);
  const searchTerm = rentalSearch.trim().toLowerCase();
  const matchesSearch = useCallback((d) => [d.customer_name, d.dispatch_number].some((value) => (value || "").toLowerCase().includes(searchTerm)), [searchTerm]);
  const filteredOnRent = useMemo(() => onRentJobs.filter(matchesSearch), [onRentJobs, matchesSearch]);
  const filteredMyRentals = useMemo(() => myNewRentals.filter(matchesSearch), [myNewRentals, matchesSearch]);
  const pendingPickups = useMemo(() => new Map(dispatches
    .filter((d) => d.job_type === "return_pickup" && ["scheduled", "delivered"].includes(d.status))
    .map((d) => [d.return_for_dispatch_id, d])), [dispatches]);
  const customerOptions = useMemo(() => [...customers].sort((a, b) => a.name.localeCompare(b.name))
    .map((c) => ({ value: c.id, label: c.name })), [customers]);

  const openPickup = (d) => {
    setPickupFor(d);
    setPickupDate("");
    setPickupNotes("");
    setPickupError("");
  };

  const submitPickup = async (e) => {
    e.preventDefault();
    if (pickupBusy) return;
    setPickupBusy(true);
    setPickupError("");
    try {
      const { data } = await api.post("/fractank/dispatches", {
        customer_id: pickupFor.customer_id,
        job_type: "return_pickup",
        return_for_dispatch_id: pickupFor.id,
        delivery_location: pickupFor.delivery_location || pickupFor.geo_address || "",
        ...(pickupFor.lat != null && pickupFor.lng != null ? { lat: pickupFor.lat, lng: pickupFor.lng } : {}),
        ...(pickupFor.company_man ? { company_man: pickupFor.company_man } : {}),
        pickup_date: pickupDate,
        notes: [`Return pickup for ${pickupFor.dispatch_number}`, pickupNotes.trim()].filter(Boolean).join("\n"),
      });
      setDispatches((prev) => [data, ...prev]);
      setPickupFor(null);
      toast.success(`Pickup request ${data.dispatch_number} sent to the manager's board`);
      loadDispatches();
    } catch (err) {
      setPickupError(errMsg(err));
    } finally {
      setPickupBusy(false);
    }
  };

  const confirmDeleteNew = async () => {
    if (deleteBusy || !deleteNew) return;
    setDeleteBusy(true);
    try {
      await api.delete(`/fractank/dispatches/${deleteNew.id}`);
      setDispatches((prev) => prev.filter((x) => x.id !== deleteNew.id));
      toast.success(`${deleteNew.dispatch_number} cancelled`);
      setDeleteNew(null);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setDeleteBusy(false);
    }
  };

  const submit = async (e) => {    e.preventDefault();
    setBusy(true);
    try {
      const payload = { customer_id: form.customer_id, delivery_location: form.delivery_location, job_type: "new_rental", rental_type: form.rental_type };
      if (form.lat !== "") payload.lat = parseFloat(form.lat);
      if (form.lng !== "") payload.lng = parseFloat(form.lng);
      payload.requester_id = form.requester_id || null;
      if (form.order_taken_date) payload.order_taken_date = form.order_taken_date;
      if (form.deliver_by_date) payload.deliver_by_date = form.deliver_by_date;
      if (form.billing_start_date) payload.billing_start_date = form.billing_start_date;
      if (form.notes) payload.notes = form.notes;
      if (form.location_id) payload.location_id = form.location_id;
      payload.initial_stage = form.initial_stage || "A";
      const r = await api.post("/fractank/dispatches", payload);
      setDone(r.data);
      setForm(EMPTY);
      toast.success(`Rental ${r.data.dispatch_number} scheduled`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background tactical-grid" data-testid="mobile-rental-page">
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-xl border-b border-slate-800 px-4 py-3 flex items-center gap-3">
        <img src="/logo.png" alt="Well Fleet Logic" className="w-9 h-9 rounded bg-white p-0.5 object-contain border border-slate-800" />
        <div>
          <p className="font-heading text-lg font-bold tracking-wide text-slate-50 leading-none">RENTAL ORDER</p>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-amber-500/80">Rental Logic</p>
        </div>
        <button data-testid="mobile-rental-logout" onClick={handleLogout} className="ml-auto flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-slate-500 hover:text-red-500 transition-colors">
          <LogOut size={15} /> Logout
        </button>
      </header>

      <main className="max-w-md mx-auto p-4 pb-12">
        {done ? (
          <div className="bg-card border border-slate-700 rounded-lg p-6 text-center space-y-3" data-testid="mobile-rental-success">
            <CheckCircle2 size={40} className="mx-auto text-emerald-500" />
            <p className="font-heading text-2xl font-bold text-slate-50">{done.dispatch_number} Scheduled</p>
            <p className="text-sm text-slate-400">{done.delivery_location}</p>
            {done.lat != null && done.lng != null && (
              <div className="pt-1 text-left" data-testid="mobile-rental-success-map">
                <MiniMap lat={done.lat} lng={done.lng} height={180} testId={`mobile-rental-confirmation-map-${done.id}`} interactive />
                <p className="text-[11px] text-slate-500 font-mono text-center pt-1">GPS pin: {done.lat}, {done.lng} — confirm this is the right spot</p>
              </div>
            )}
            <ActionButton testId="mobile-rental-another-button" onClick={() => { setDone(null); setForm(EMPTY); setMapsLink(""); loadData(); }} className="w-full justify-center">
              Submit Another Rental
            </ActionButton>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="mobile-rental-form">
            <div className="space-y-3 pt-2">
              <p data-testid="mobile-rental-signed-in" className="text-xs text-slate-500 font-mono uppercase tracking-widest break-words">Signed in as {user?.name}</p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  data-testid="mobile-rental-my-rentals-toggle"
                  aria-expanded={showMyRentals}
                  aria-controls="mobile-rental-my-rentals-list"
                  onClick={() => { if (!showMyRentals) loadDispatches(); setShowMyRentals((s) => !s); }}
                  className={`text-xs font-mono uppercase tracking-wider text-amber-500 hover:bg-amber-500/10 border border-amber-500/40 rounded-md px-2 py-3 transition-colors ${showMyRentals ? "bg-amber-500/10" : "bg-card"}`}
                >
                  My New Rentals ({searchTerm ? `${filteredMyRentals.length}/${myNewRentals.length}` : myNewRentals.length})
                </button>
                <button
                  type="button"
                  data-testid="mobile-rental-on-rent-toggle"
                  aria-expanded={showOnRent}
                  aria-controls="mobile-rental-on-rent-list"
                  onClick={() => { if (!showOnRent) loadDispatches(); setShowOnRent((s) => !s); }}
                  className={`text-xs font-mono uppercase tracking-wider text-amber-500 hover:bg-amber-500/10 border border-amber-500/40 rounded-md px-2 py-3 transition-colors ${showOnRent ? "bg-amber-500/10" : "bg-card"}`}
                >
                  On Rent ({searchTerm ? `${filteredOnRent.length}/${onRentJobs.length}` : onRentJobs.length})
                </button>
              </div>
              <div className="relative" role="search" aria-label="Search rentals">
                <Search size={16} aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
                <Input type="search" data-testid="mobile-rental-search-input" aria-label="Search rentals by customer or ticket ID" placeholder="Search customer or ticket ID" value={rentalSearch} onChange={(e) => setRentalSearch(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }} className="h-11 pl-9 pr-11 bg-card border-slate-700 text-sm [&::-webkit-search-cancel-button]:hidden" />
                {rentalSearch && <button type="button" data-testid="mobile-rental-search-clear" aria-label="Clear rental search" onClick={() => setRentalSearch("")} className="absolute right-0 top-0 h-11 w-11 flex items-center justify-center text-slate-500 hover:text-amber-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 rounded-md transition-colors"><X size={16} /></button>}
              </div>
              {searchTerm && <p data-testid="mobile-rental-search-results" role="status" className="text-xs text-slate-500">{filteredOnRent.length} On Rent and {filteredMyRentals.length} My New Rentals match your search.</p>}
            </div>
            {(showMyRentals || showOnRent) && !rentalsLoaded && <p data-testid="mobile-rental-list-loading" role="status" className="text-xs text-slate-500">Loading rentals…</p>}
            {(showMyRentals || showOnRent) && rentalsError && (
              <div data-testid="mobile-rental-list-error" role="alert" className="text-xs text-red-500 space-y-2">
                <p>Unable to load rentals: {rentalsError}</p>
                <ActionButton type="button" testId="mobile-rental-list-retry" variant="secondary" onClick={loadDispatches}>Try again</ActionButton>
              </div>
            )}
            {showMyRentals && rentalsLoaded && !rentalsError && (
              <div id="mobile-rental-my-rentals-list" className="bg-card border border-slate-700 rounded-lg p-3 space-y-2 [overflow-wrap:anywhere]" data-testid="mobile-rental-my-rentals-list">
                {filteredMyRentals.length === 0 ? (
                  <p data-testid="mobile-rental-my-rentals-empty" className="text-xs text-slate-500 text-center py-2">{searchTerm ? "No new rentals match your search." : "You haven't created any new rentals yet."}</p>
                ) : (
                  filteredMyRentals.map((d) => (
                    <div key={d.id} data-testid={`mobile-rental-my-rental-${d.id}`} className={`border rounded-md p-2 ${d.rental_type === "mixing_plant" ? "bg-pink-50 border-pink-300" : "border-slate-200"}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-sm text-slate-100">{d.dispatch_number}</span>
                        <RentalTypeBadge job={d} prefix="sales-new" />
                        <span className="text-[10px] font-mono uppercase tracking-wider text-amber-500 bg-amber-500/10 border border-amber-500/30 rounded px-1.5 py-0.5">New</span>
                      </div>
                      <p className="text-xs text-slate-400">{d.customer_name}</p>
                      <p className="text-xs text-slate-500">{d.delivery_location || d.geo_address || "No location"}</p>
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        <ActionButton type="button" variant="secondary" testId={`mobile-rental-edit-new-${d.id}`} onClick={() => setEditNew(d)} className="justify-center !py-2 !text-xs">Edit Ticket</ActionButton>
                        <ActionButton type="button" variant="secondary" testId={`mobile-rental-edit-wells-new-${d.id}`} onClick={() => setEditOnRent(d)} className="justify-center !py-2 !text-xs">Tank Days / Wells</ActionButton>
                      </div>
                      <ActionButton type="button" variant="secondary" testId={`mobile-rental-delete-new-${d.id}`} onClick={() => setDeleteNew(d)} className="w-full justify-center mt-2 !py-2 !text-xs !text-red-500 hover:!bg-red-500/10 hover:!border-red-500/40">Cancel Rental</ActionButton>
                    </div>
                  ))
                )}
              </div>
            )}
            {showOnRent && rentalsLoaded && !rentalsError && (
              <section id="mobile-rental-on-rent-list" data-testid="mobile-rental-on-rent-list" className="space-y-3" aria-label="All company rentals on rent">
                <p data-testid="mobile-rental-on-rent-heading" className="text-xs text-slate-500 font-mono uppercase tracking-wider">All company rentals · On Rent</p>
                {filteredOnRent.length === 0 ? (
                  <p data-testid="mobile-rental-on-rent-empty" className="bg-card border border-slate-700 rounded-lg text-xs text-slate-500 text-center p-4">{searchTerm ? "No On Rent rentals match your search." : "No rentals are currently On Rent."}</p>
                ) : filteredOnRent.map((d) => <OnRentCard key={d.id} dispatch={d} onSchedulePickup={openPickup} onEdit={setEditOnRent} pickupRequest={pendingPickups.get(d.id)} />)}
              </section>
            )}
            <Field label="Rental Type"><SelectInput testId="mobile-rental-type-select" value={form.rental_type} onChange={(e) => setForm({ ...form, rental_type: e.target.value })} options={JOB_TYPE_OPTIONS} className="w-full min-w-0 max-w-full !py-3 !text-base" /></Field>
            <Field label="Customer">
              <SelectInput testId="mobile-rental-customer-select" value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value, delivery_location: "", location_id: "", location_name: "", requester_id: "", company_man: "" })} required placeholder="Select customer" className="w-full min-w-0 max-w-full !py-3 !text-base" options={customerOptions} />
            </Field>
            <LocationPicker testId="mobile-rental" customerId={form.customer_id} value={form.location_id} snapshotName={form.location_name || form.delivery_location} onChange={(loc) => setForm((prev) => ({ ...prev, location_id: loc?.id || "", location_name: loc?.name || "", delivery_location: loc ? (loc.name + (loc.wells?.length ? " - " + loc.wells.join(", ") : "")) : "", lat: loc?.lat != null ? String(loc.lat) : prev.lat, lng: loc?.lng != null ? String(loc.lng) : prev.lng }))} />
            <Field label="Starting Well Stage (editable later)"><SelectInput testId="mobile-rental-stage-select" value={form.initial_stage || "A"} onChange={(e) => setForm({ ...form, initial_stage: e.target.value })} className="w-full min-w-0 max-w-full !py-3 !text-base" options={[{ value: "A", label: "Stage A" }, { value: "B", label: "Stage B" }, { value: "C", label: "Stage C" }, { value: "D", label: "Stage D" }]} /></Field>
            <Field label="Google Maps link (share → copy link → paste here)">
              <TextInput testId="mobile-rental-maps-link" value={mapsLink} onChange={(e) => setMapsLink(e.target.value)} placeholder={resolving ? "Resolving…" : "Paste a Google Maps link to fill GPS"} className="w-full min-w-0 max-w-full !py-3 !text-base" />
            </Field>
            <div className="grid grid-cols-2 gap-3 [&>label]:min-w-0">
              <Field label="Latitude (GPS)">
                <TextInput testId="mobile-rental-lat" type="number" step="any" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} className="w-full min-w-0 max-w-full !py-3 !text-base" placeholder="31.9973" />
              </Field>
              <Field label="Longitude (GPS)">
                <TextInput testId="mobile-rental-lng" type="number" step="any" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} className="w-full min-w-0 max-w-full !py-3 !text-base" placeholder="-102.0779" />
              </Field>
            </div>
            <RequesterPicker testId="mobile-rental" customerId={form.customer_id} value={form.requester_id} snapshotName={form.company_man} onChange={(r) => setForm((f) => ({ ...f, requester_id: r?.id || "", company_man: r?.name || "" }))} />
            <Field label="Order taken date">
              <TextInput testId="mobile-rental-order-taken" type="date" value={form.order_taken_date} onChange={(e) => setForm({ ...form, order_taken_date: e.target.value })} className="w-full min-w-0 max-w-full !py-3 !text-base" />
            </Field>
            <Field label="Date to be delivered">
              <TextInput testId="mobile-rental-deliver-by" type="date" value={form.deliver_by_date} onChange={(e) => setForm({ ...form, deliver_by_date: e.target.value })} className="w-full min-w-0 max-w-full !py-3 !text-base" />
            </Field>
            <Field label="Date billing rental starts">
              <TextInput testId="mobile-rental-billing-start" type="date" value={form.billing_start_date} onChange={(e) => setForm({ ...form, billing_start_date: e.target.value })} className="w-full min-w-0 max-w-full !py-3 !text-base" />
            </Field>
            <Field label="Notes">
              <TextArea testId="mobile-rental-notes" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full min-w-0 max-w-full !text-base" placeholder="Press Enter for a new line…" />
            </Field>
            <ActionButton testId="mobile-rental-submit" type="submit" disabled={busy} className="w-full justify-center !py-4 !text-base">
              {busy ? "Scheduling…" : "Schedule Rental"}
            </ActionButton>
          </form>
        )}
      </main>
      <FormDialog open={!!editOnRent} onOpenChange={(open) => { if (!open) setEditOnRent(null); }} title={`Tank Days — ${editOnRent?.dispatch_number || ""}`} testId="mobile-rental-edit-dialog">
        {editOnRent && <WellBillingEditor dispatch={editOnRent} testId="mobile-rental-wb" onSaved={(d) => { setEditOnRent(null); setDispatches((prev) => prev.map((x) => (x.id === d.id ? { ...x, ...d } : x))); loadDispatches(); }} />}
      </FormDialog>
      <FormDialog open={!!editNew} onOpenChange={(open) => { if (!open) setEditNew(null); }} title={`Edit ${editNew?.dispatch_number || ""}`} testId="mobile-rental-edit-new-dialog">
        {editNew && <RentalEditForm dispatch={editNew} customerOptions={customerOptions} onCancel={() => setEditNew(null)} onSaved={(d) => { setEditNew(null); setDispatches((prev) => prev.map((x) => (x.id === d.id ? d : x))); loadDispatches(); }} />}
      </FormDialog>
      <FormDialog open={!!deleteNew} onOpenChange={(open) => { if (!open && !deleteBusy) setDeleteNew(null); }} title={`Cancel ${deleteNew?.dispatch_number || ""}?`} testId="mobile-rental-delete-new-dialog">
        {deleteNew && (
          <div className="space-y-4" data-testid="mobile-rental-delete-new-body">
            <p className="text-sm text-slate-400">This permanently removes the new rental <span className="font-mono text-slate-200">{deleteNew.dispatch_number}</span> for {deleteNew.customer_name}. This cannot be undone.</p>
            <div className="flex gap-2">
              <ActionButton type="button" variant="secondary" testId="mobile-rental-delete-new-cancel" disabled={deleteBusy} onClick={() => setDeleteNew(null)}>Keep Rental</ActionButton>
              <ActionButton type="button" testId="mobile-rental-delete-new-confirm" disabled={deleteBusy} onClick={confirmDeleteNew} className="flex-1 justify-center !bg-red-600 hover:!bg-red-700">{deleteBusy ? "Cancelling…" : "Cancel Rental"}</ActionButton>
            </div>
          </div>
        )}
      </FormDialog>
      <FormDialog open={!!pickupFor} onOpenChange={(open) => { if (!open && !pickupBusy) setPickupFor(null); }} title={`Schedule Pickup — ${pickupFor?.dispatch_number || ""}`} testId="mobile-rental-pickup-dialog">
        {pickupFor && (
          <form onSubmit={submitPickup} data-testid="mobile-rental-pickup-form" className="space-y-4">
            <div data-testid="mobile-rental-pickup-summary" className="rounded-md border border-slate-700 bg-slate-900/60 p-3 space-y-1 [overflow-wrap:anywhere]">
              <p className="text-sm font-medium text-slate-100">{pickupFor.customer_name}</p>
              <p className="text-xs text-slate-400">{pickupFor.delivery_location || pickupFor.geo_address || "No location"}</p>
              {pickupFor.asset_tags?.length > 0 && <p className="text-xs text-slate-500">Equipment: {pickupFor.asset_tags.join(", ")}</p>}
            </div>
            <p data-testid="mobile-rental-pickup-help" className="text-xs text-slate-500">Send a pickup request to the manager. No driver is assigned here, and the original rental stays On Rent.</p>
            <Field label="Pickup date">
              <TextInput testId="mobile-rental-pickup-date" type="date" value={pickupDate} onChange={(e) => setPickupDate(e.target.value)} required disabled={pickupBusy} />
            </Field>
            <Field label="Notes (optional)">
              <textarea data-testid="mobile-rental-pickup-notes" value={pickupNotes} onChange={(e) => setPickupNotes(e.target.value)} disabled={pickupBusy} rows={3} placeholder="Pickup instructions, gate code, or on-site contact…" className="w-full rounded-md border border-slate-700 bg-slate-900/80 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-amber-500/60 focus:ring-1 focus:ring-amber-500/40 transition-colors" />
            </Field>
            {pickupError && <p data-testid="mobile-rental-pickup-error" role="alert" className="text-sm text-red-500 break-words">{pickupError}</p>}
            <div className="flex gap-2">
              <ActionButton testId="mobile-rental-pickup-cancel" type="button" variant="secondary" disabled={pickupBusy} onClick={() => setPickupFor(null)}>Cancel</ActionButton>
              <ActionButton testId="mobile-rental-pickup-submit" type="submit" disabled={pickupBusy} className="flex-1 justify-center">{pickupBusy ? "Sending…" : "Send Pickup Request"}</ActionButton>
            </div>
          </form>
        )}
      </FormDialog>
    </div>
  );
}
